import React, { useState, useEffect, useMemo } from 'react';
import { Component, TestCase, TestStep, Feature, TestCaseStatus, TestCaseComment } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import type { AppMessage } from '../../utils/messages';
import { apiRequest } from '../../utils/api';

interface AddTestCaseModalProps {
  allComponents: Component[];
  allFeatures?: Feature[];
  isOpen: boolean;
  onClose: () => void;
  onTestCaseAdded: (testCase: TestCase) => Promise<void> | void;
  onTestCaseUpdated?: (testCase: TestCase) => Promise<void> | void;
  showMessage: (msg: AppMessage) => void;
  prefillData?: {
    componentId?: string;
    featureId?: string;
  };
  editingTestCase?: TestCase | null;
}

const AddTestCaseModal: React.FC<AddTestCaseModalProps> = ({
  allComponents,
  allFeatures = [],
  isOpen,
  onClose,
  onTestCaseAdded,
  onTestCaseUpdated,
  showMessage,
  prefillData,
  editingTestCase
}) => {
  const { user } = useAuth();
  const [testCaseTitle, setTestCaseTitle] = useState('');
  const [testCaseDescription, setTestCaseDescription] = useState('');
  const [testCasePreConditions, setTestCasePreConditions] = useState('');
  const [selectedComponentId, setSelectedComponentId] = useState<string>(prefillData?.componentId || '');
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>(prefillData?.featureId || '');
  const [steps, setSteps] = useState<TestStep[]>([]);
  const [showStepsSection, setShowStepsSection] = useState(false);
  const [isAutomated, setIsAutomated] = useState(false);
  const [isAutomationCandidate, setIsAutomationCandidate] = useState(false);
  const [status, setStatus] = useState<TestCaseStatus>('Active');
  const [comments, setComments] = useState<TestCaseComment[]>([]);
  const [newComment, setNewComment] = useState('');

  // Get filtered components based on selected feature
  const filteredComponents = useMemo(() => {
    if (!selectedFeatureId) return allComponents;
    return allComponents.filter(c => c.featureId === selectedFeatureId);
  }, [selectedFeatureId, allComponents]);

  // Reset form when modal opens/closes or prefillData/editingTestCase changes
  useEffect(() => {
    if (isOpen) {
      if (editingTestCase) {
        setTestCaseTitle(editingTestCase.title || '');
        setTestCaseDescription(editingTestCase.description || '');
        setTestCasePreConditions(editingTestCase.preConditions || '');
        setSelectedComponentId(editingTestCase.componentId || '');
        // Derive featureId from component if not set directly
        const componentFeatureId = editingTestCase.componentId 
          ? allComponents.find(c => c.id === editingTestCase.componentId)?.featureId 
          : null;
        setSelectedFeatureId(editingTestCase.featureId || componentFeatureId || '');
        setSteps(editingTestCase.steps || []);
        setShowStepsSection((editingTestCase.steps || []).length > 0);
        setIsAutomated(editingTestCase.automated || false);
        setIsAutomationCandidate(editingTestCase.automationCandidate || false);
        setStatus(editingTestCase.status || 'Active');
        setComments(editingTestCase.comments || []);
        setNewComment('');
      } else {
        setTestCaseTitle('');
        setTestCaseDescription('');
        setTestCasePreConditions('');
        setSelectedComponentId(prefillData?.componentId || '');
        // Derive featureId from componentId if featureId not provided
        const derivedFeatureId = prefillData?.featureId || (
          prefillData?.componentId 
            ? allComponents.find(c => c.id === prefillData.componentId)?.featureId 
            : ''
        ) || '';
        setSelectedFeatureId(derivedFeatureId);
        setSteps([]);
        setShowStepsSection(false);
        setIsAutomated(false);
        setIsAutomationCandidate(false);
        setStatus('Active');
        setComments([]);
        setNewComment('');
      }
    }
  }, [isOpen, prefillData, editingTestCase, allComponents]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleAddTestCase = async () => {
    if (!testCaseTitle.trim()) {
      showMessage({ type: 'error', text: 'Please enter a test case title.' });
      return;
    }

    if (!selectedFeatureId) {
      showMessage({ type: 'error', text: 'Please select a feature for this test case.' });
      return;
    }

    if (!selectedComponentId) {
      showMessage({ type: 'error', text: 'Please select a component for this test case.' });
      return;
    }

    // Validate: If status is "Needs Update", require a comment
    if (status === 'Needs Update' && comments.length === 0) {
      showMessage({ type: 'error', text: 'Please add a comment explaining why this test case needs an update.' });
      return;
    }

    try {
      // Filter out empty steps
      const validSteps = steps.filter(step => step.action.trim());

      if (editingTestCase) {
        // Update existing test case (comments are saved independently via API)
        const updatedTestCase = await apiRequest<TestCase>(`/api/testcases/${editingTestCase.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            title: testCaseTitle.trim(),
            componentId: selectedComponentId || undefined,
            featureId: selectedFeatureId || undefined,
            description: testCaseDescription.trim() || undefined,
            preConditions: testCasePreConditions.trim() || undefined,
            steps: validSteps.length > 0 ? validSteps : undefined,
            automated: isAutomated,
            automationCandidate: isAutomationCandidate,
            status
            // Note: comments are NOT included - they're saved independently via handleAddCommentImmediately
          })
        }, {
          useAuth: true,
          setMessage: undefined
        });

        // Reset form and close modal
        setTestCaseTitle('');
        setTestCaseDescription('');
        setTestCasePreConditions('');
        setSteps([]);
        setShowStepsSection(false);
        setIsAutomated(false);
        setIsAutomationCandidate(false);
        setStatus('Active');
        setComments([]);
        setNewComment('');
        setSelectedComponentId(prefillData?.componentId || '');
        setSelectedFeatureId(prefillData?.featureId || '');
        onClose();

        // Call the callback to handle updating the test case
        if (onTestCaseUpdated) {
          const result = onTestCaseUpdated(updatedTestCase);
          if (result instanceof Promise) {
            result.catch((error: unknown) => {
              console.error('Error updating test case:', error);
              if (error instanceof Error) {
                showMessage({ type: 'error', text: error.message || 'Failed to update test case. Please try again.' });
              } else {
                showMessage({ type: 'error', text: 'Failed to update test case. Please try again.' });
              }
            });
          }
        }
      } else {
        // Create new test case - include local comments since they can't be saved via API yet
        const newTestCase = await apiRequest<TestCase>('/api/testcases', {
          method: 'POST',
          body: JSON.stringify({
            title: testCaseTitle.trim(),
            componentId: selectedComponentId || undefined,
            featureId: selectedFeatureId || undefined,
            description: testCaseDescription.trim() || undefined,
            preConditions: testCasePreConditions.trim() || undefined,
            steps: validSteps.length > 0 ? validSteps : undefined,
            automated: isAutomated,
            automationCandidate: isAutomationCandidate,
            status,
            comments: comments // Include local comments for new test cases
          })
        }, {
          useAuth: true,
          setMessage: undefined // Don't show message here, parent will handle it
        });

        // Reset form and close modal
        setTestCaseTitle('');
        setTestCaseDescription('');
        setTestCasePreConditions('');
        setSteps([]);
        setShowStepsSection(false);
        setIsAutomated(false);
        setIsAutomationCandidate(false);
        setStatus('Active');
        setComments([]);
        setNewComment('');
        setSelectedComponentId(prefillData?.componentId || '');
        setSelectedFeatureId(prefillData?.featureId || '');
        onClose();

        // Call the callback to handle adding the test case
        const result = onTestCaseAdded(newTestCase);
        if (result instanceof Promise) {
          result.catch((error: unknown) => {
            console.error('Error adding test case:', error);
            if (error instanceof Error) {
              showMessage({ type: 'error', text: error.message || 'Failed to add test case. Please try again.' });
            } else {
              showMessage({ type: 'error', text: 'Failed to add test case. Please try again.' });
            }
          });
        }
      }
    } catch (error) {
      console.error('Error creating/updating test case:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      showMessage({ type: 'error', text: `Failed to ${editingTestCase ? 'update' : 'create'} test case: ${errorMsg}` });
    }
  };

  // Comment management - comments are saved independently for existing test cases
  const handleAddCommentImmediately = async () => {
    if (!newComment.trim()) return;
    
    if (editingTestCase?.id) {
      // For existing test cases, save to API immediately
      try {
        const savedComment = await apiRequest<TestCaseComment>(`/api/testcases/${editingTestCase.id}/comments`, {
          method: 'POST',
          body: JSON.stringify({ text: newComment.trim() })
        }, { useAuth: true });
        
        setComments([...comments, savedComment]);
        setNewComment('');
      } catch (error) {
        console.error('Error adding comment:', error);
        showMessage({ type: 'error', text: 'Failed to add comment' });
      }
    } else {
      // For new test cases, just add locally - will be saved with test case
      const comment: TestCaseComment = {
        id: `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        userId: user?.id || '',
        userName: user?.displayName || user?.email || 'Unknown',
        text: newComment.trim(),
        createdAt: new Date().toISOString()
      };
      setComments([...comments, comment]);
      setNewComment('');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (editingTestCase?.id) {
      // For existing test cases, delete via API
      try {
        await apiRequest(`/api/testcases/${editingTestCase.id}/comments/${commentId}`, {
          method: 'DELETE'
        }, { useAuth: true });
        setComments(comments.filter(c => c.id !== commentId));
      } catch (error) {
        console.error('Error deleting comment:', error);
        showMessage({ type: 'error', text: 'Failed to delete comment' });
      }
    } else {
      // For new test cases, just remove locally
      setComments(comments.filter(c => c.id !== commentId));
    }
  };

  // Step management functions
  const handleAddStep = () => {
    const newStep: TestStep = {
      id: `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      action: '',
      data: '',
      expectedResult: '',
      order: steps.length + 1
    };
    setSteps([...steps, newStep]);
  };

  const handleUpdateStep = (stepId: string, field: 'action' | 'data' | 'expectedResult', value: string) => {
    setSteps(steps.map(step => 
      step.id === stepId ? { ...step, [field]: value } : step
    ));
  };

  const handleRemoveStep = (stepId: string) => {
    const newSteps = steps.filter(step => step.id !== stepId);
    // Re-order steps
    setSteps(newSteps.map((step, index) => ({ ...step, order: index + 1 })));
  };

  const [draggingStepId, setDraggingStepId] = useState<string | null>(null);
  const [dragOverStepId, setDragOverStepId] = useState<string | null>(null);

  const handleStepDragStart = (e: React.DragEvent, stepId: string) => {
    setDraggingStepId(stepId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleStepDragOver = (e: React.DragEvent, stepId: string) => {
    e.preventDefault();
    if (draggingStepId && draggingStepId !== stepId) {
      setDragOverStepId(stepId);
    }
  };

  const handleStepDragLeave = () => {
    setDragOverStepId(null);
  };

  const handleStepDrop = (e: React.DragEvent, targetStepId: string) => {
    e.preventDefault();
    if (!draggingStepId || draggingStepId === targetStepId) return;

    const dragIndex = steps.findIndex(s => s.id === draggingStepId);
    const dropIndex = steps.findIndex(s => s.id === targetStepId);
    
    if (dragIndex === -1 || dropIndex === -1) return;

    const newSteps = [...steps];
    const [removed] = newSteps.splice(dragIndex, 1);
    newSteps.splice(dropIndex, 0, removed);
    
    // Re-order steps
    setSteps(newSteps.map((step, index) => ({ ...step, order: index + 1 })));
    setDraggingStepId(null);
    setDragOverStepId(null);
  };

  const handleStepDragEnd = () => {
    setDraggingStepId(null);
    setDragOverStepId(null);
  };

  if (!isOpen) return null;

  const modalStyles = {
    container: {
      width: '1200px',
      maxWidth: '95vw',
      maxHeight: '85vh',
      display: 'flex',
      flexDirection: 'column' as const,
      padding: 0,
      overflow: 'hidden'
    },
    header: {
      padding: '1.5rem 2rem',
      borderBottom: '1px solid var(--border-color)',
      background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, transparent 100%)'
    },
    title: {
      margin: 0,
      fontSize: '1.5rem',
      fontWeight: 600,
      color: 'var(--cyan-glow)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    bodyWrapper: {
      flex: 1,
      display: 'grid',
      gridTemplateColumns: '1fr 320px',
      overflow: 'hidden'
    },
    body: {
      flex: 1,
      overflowY: 'auto' as const,
      padding: '1.5rem 2rem'
    },
    commentsSidebar: {
      borderLeft: '1px solid var(--border-color)',
      display: 'flex',
      flexDirection: 'column' as const,
      backgroundColor: 'var(--surface-elevated)',
      overflow: 'hidden'
    },
    footer: {
      padding: '1.25rem 2rem',
      borderTop: '1px solid var(--border-color)',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      background: 'var(--surface-elevated)'
    },
    section: {
      marginBottom: '1.5rem'
    },
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--text-secondary)',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      marginBottom: '0.75rem'
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1.5rem'
    }
  };

  return (
    <div 
      className="modal-overlay"
      style={{
        overflow: 'hidden',
        pointerEvents: 'auto'
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.preventDefault()}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="card modal-content" onClick={(e) => e.stopPropagation()} style={modalStyles.container}>
        {/* Header */}
        <div style={{ ...modalStyles.header, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={modalStyles.title}>
            <span style={{ 
              width: '32px', 
              height: '32px', 
              borderRadius: '8px',
              background: 'linear-gradient(135deg, var(--cyan-glow) 0%, var(--primary-color) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem'
            }}>
              {editingTestCase ? '✏️' : '📋'}
            </span>
            {editingTestCase ? 'Edit Test Case' : 'Create Test Case'}
          </h2>
          
          {/* Status Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status:</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as TestCaseStatus)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: '1px solid var(--border-color)',
                backgroundColor: status === 'Active' ? 'var(--success-transparent, rgba(34, 197, 94, 0.1))' 
                  : status === 'Draft' ? 'var(--cyan-transparent)' 
                  : 'var(--danger-transparent, rgba(239, 68, 68, 0.1))',
                color: status === 'Active' ? 'var(--success, #22c55e)' 
                  : status === 'Draft' ? 'var(--cyan-glow)' 
                  : 'var(--danger)',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
                appearance: 'none',
                paddingRight: '2rem',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2300d4ff' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.5rem center'
              }}
            >
              <option value="Active">Active</option>
              <option value="Draft">Draft</option>
              <option value="Needs Update">Needs Update</option>
            </select>
          </div>
        </div>

        {/* Body Wrapper with Comments Sidebar */}
        <div style={modalStyles.bodyWrapper}>
          {/* Main Form Content */}
          <div style={modalStyles.body}>
          {/* Basic Info Section */}
          <div style={modalStyles.section}>
            <div style={modalStyles.sectionTitle}>Basic Information</div>
            
            <div style={{ ...modalStyles.row, marginBottom: '1rem' }}>
              {/* Title */}
              <div style={{ gridColumn: '1 / -1' }}>
          <label className="label">Test Case Title <span className="text-danger">*</span></label>
          <input
            type="text"
                  placeholder="e.g., Verify user can successfully log in with valid credentials"
            value={testCaseTitle}
            onChange={(e) => setTestCaseTitle(e.target.value)}
            className="form-control"
                  style={{ fontSize: '1rem' }}
            onKeyPress={(e) => e.key === 'Enter' && handleAddTestCase()}
                  autoFocus
          />
        </div>
        </div>

<div style={modalStyles.row}>
              {/* Feature */}
              <div>
                <label className="label">Feature <span className="text-danger">*</span></label>
                <select
                  value={selectedFeatureId}
                  onChange={(e) => {
                    setSelectedFeatureId(e.target.value);
                    // Clear component when feature changes (unless component belongs to new feature)
                    if (selectedComponentId) {
                      const currentComponent = allComponents.find(c => c.id === selectedComponentId);
                      if (currentComponent && currentComponent.featureId !== e.target.value) {
                        setSelectedComponentId('');
                      }
                    }
                  }}
                  className="form-control"
                  required
                >
                  <option value="">Select a feature...</option>
                  {allFeatures.map(feature => (
                    <option key={feature.id} value={feature.id}>
                      {feature.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Component */}
              <div>
          <label className="label">Component <span className="text-danger">*</span></label>
          <select
            value={selectedComponentId}
                  onChange={(e) => {
                    setSelectedComponentId(e.target.value);
                    // Auto-select feature if component is selected
                    if (e.target.value) {
                      const component = allComponents.find(c => c.id === e.target.value);
                      if (component && component.featureId !== selectedFeatureId) {
                        setSelectedFeatureId(component.featureId);
                      }
                    }
                  }}
            className="form-control"
                  disabled={!selectedFeatureId}
                >
                  <option value="">
                    {!selectedFeatureId 
                      ? 'Select a feature first...' 
                      : filteredComponents.length === 0 
                        ? 'No components in this feature' 
                        : 'Select a component...'}
                  </option>
                  {filteredComponents.map(component => (
              <option key={component.id} value={component.id}>
                {component.name}
              </option>
            ))}
          </select>
        </div>
            </div>
          </div>

          {/* Description & Pre-Conditions Section */}
          <div style={modalStyles.section}>
            <div style={modalStyles.row}>
              <div>
                <div style={modalStyles.sectionTitle}>Description</div>
                <textarea
                  value={testCaseDescription}
                  onChange={(e) => setTestCaseDescription(e.target.value)}
                  className="form-control"
                  rows={4}
                  placeholder="Describe the purpose and scope of this test case..."
                  style={{ resize: 'vertical', minHeight: '100px' }}
                />
              </div>
              <div>
                <div style={modalStyles.sectionTitle}>Pre-Conditions</div>
                <textarea
                  value={testCasePreConditions}
                  onChange={(e) => setTestCasePreConditions(e.target.value)}
                  className="form-control"
                  rows={4}
                  placeholder="Prerequisites or setup required before executing this test case (e.g., user must be logged in, specific data must exist)..."
                  style={{ resize: 'vertical', minHeight: '100px' }}
                />
              </div>
            </div>
          </div>

          {/* Automation Section */}
          <div style={modalStyles.section}>
            <div style={modalStyles.sectionTitle}>Automation Status</div>
            <div style={{ 
              display: 'flex', 
              gap: '2rem', 
              padding: '1rem',
              background: 'var(--surface-elevated)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)'
            }}>
              {/* Automated Toggle */}
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                cursor: 'pointer',
                flex: 1
              }}>
                <div 
                  onClick={() => {
                    setIsAutomated(!isAutomated);
                    // If marking as automated, remove from candidates
                    if (!isAutomated) {
                      setIsAutomationCandidate(false);
                    }
                  }}
                  style={{
                    width: '48px',
                    height: '26px',
                    borderRadius: '13px',
                    backgroundColor: isAutomated ? 'var(--success)' : 'var(--border-color)',
                    position: 'relative',
                    transition: 'background-color 0.2s ease',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: '2px',
                    left: isAutomated ? '24px' : '2px',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }} />
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                    Automated
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    This test case has automation coverage
                  </div>
                </div>
                {isAutomated && (
                  <span style={{ 
                    marginLeft: 'auto',
                    padding: '0.25rem 0.5rem',
                    background: 'var(--success)',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}>
                    ✓ AUTOMATED
                  </span>
                )}
              </label>

              {/* Automation Candidate Toggle */}
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                cursor: isAutomated ? 'not-allowed' : 'pointer',
                flex: 1,
                opacity: isAutomated ? 0.5 : 1
              }}>
                <div 
                  onClick={() => {
                    if (!isAutomated) {
                      setIsAutomationCandidate(!isAutomationCandidate);
                    }
                  }}
                  style={{
                    width: '48px',
                    height: '26px',
                    borderRadius: '13px',
                    backgroundColor: isAutomationCandidate ? 'var(--warning)' : 'var(--border-color)',
                    position: 'relative',
                    transition: 'background-color 0.2s ease',
                    cursor: isAutomated ? 'not-allowed' : 'pointer'
                  }}
                >
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: '2px',
                    left: isAutomationCandidate ? '24px' : '2px',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }} />
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                    Ready to Automate
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Good candidate for future automation
                  </div>
                </div>
                {isAutomationCandidate && !isAutomated && (
                  <span style={{ 
                    marginLeft: 'auto',
                    padding: '0.25rem 0.5rem',
                    background: 'var(--warning)',
                    color: 'var(--surface)',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}>
                    🎯 CANDIDATE
                  </span>
                )}
              </label>
            </div>
          </div>

          {/* Test Steps Section */}
          <div style={modalStyles.section}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <div style={modalStyles.sectionTitle}>
                Test Steps
                {steps.length > 0 && (
                  <span style={{ 
                    marginLeft: '0.5rem', 
                    padding: '0.125rem 0.5rem',
                    background: 'var(--cyan-glow)',
                    color: 'var(--surface)',
                    borderRadius: '10px',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    {steps.length}
                  </span>
                )}
              </div>
              {!showStepsSection && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowStepsSection(true);
                    if (steps.length === 0) {
                      handleAddStep();
                    }
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <span>+</span> Add Test Steps
                </button>
              )}
            </div>
            
            {showStepsSection ? (
              <div style={{ 
                border: '1px solid var(--border-color)', 
                borderRadius: '8px', 
                overflow: 'hidden',
                backgroundColor: 'var(--surface-elevated)'
              }}>
                {/* Steps header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '32px 1.5fr 1fr 1fr 36px',
                  gap: '0.75rem',
                  padding: '0.625rem 1rem',
                  background: 'var(--surface)',
                  borderBottom: '1px solid var(--border-color)',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <div style={{ textAlign: 'center' }}>#</div>
                  <div>Action</div>
                  <div>Data</div>
                  <div>Expected Result</div>
                  <div></div>
                </div>

                {/* Steps list */}
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {steps.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      color: 'var(--text-muted)', 
                      padding: '2rem',
                      fontSize: '0.875rem'
                    }}>
                      No steps added yet. Click "Add Step" to begin.
                    </div>
                  ) : (
                    steps.map((step, index) => (
                      <div 
                        key={step.id}
                        draggable
                        onDragStart={(e) => handleStepDragStart(e, step.id)}
                        onDragOver={(e) => handleStepDragOver(e, step.id)}
                        onDragLeave={handleStepDragLeave}
                        onDrop={(e) => handleStepDrop(e, step.id)}
                        onDragEnd={handleStepDragEnd}
                        style={{ 
                          display: 'grid',
                          gridTemplateColumns: '32px 1.5fr 1fr 1fr 36px',
                          gap: '0.75rem',
                          padding: '0.625rem 1rem',
                          alignItems: 'center',
                          borderBottom: index < steps.length - 1 ? '1px solid var(--border-color)' : 'none',
                          background: draggingStepId === step.id 
                            ? 'rgba(0, 212, 255, 0.15)' 
                            : dragOverStepId === step.id 
                              ? 'rgba(0, 212, 255, 0.08)' 
                              : 'transparent',
                          cursor: 'grab',
                          opacity: draggingStepId === step.id ? 0.7 : 1,
                          transition: 'background 0.15s ease, opacity 0.15s ease',
                          borderLeft: dragOverStepId === step.id ? '3px solid var(--cyan-glow)' : '3px solid transparent'
                        }}
                      >
                        {/* Step number with drag handle */}
                        <div style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '4px', 
                          background: 'var(--cyan-glow)',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          flexShrink: 0,
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          {index + 1}
                        </div>

                        {/* Action input */}
                        <input
                          type="text"
                          placeholder="What action to perform?"
                          value={step.action}
                          onChange={(e) => handleUpdateStep(step.id, 'action', e.target.value)}
                          className="form-control"
                          style={{ fontSize: '0.8rem', marginBottom: 0, padding: '0.5rem 0.75rem' }}
                        />

                        {/* Data input */}
                        <input
                          type="text"
                          placeholder="Test data/input"
                          value={step.data || ''}
                          onChange={(e) => handleUpdateStep(step.id, 'data', e.target.value)}
                          className="form-control"
                          style={{ fontSize: '0.8rem', marginBottom: 0, padding: '0.5rem 0.75rem' }}
                        />
                        
                        {/* Expected result input */}
          <input
            type="text"
                          placeholder="Expected result"
                          value={step.expectedResult || ''}
                          onChange={(e) => handleUpdateStep(step.id, 'expectedResult', e.target.value)}
            className="form-control"
                          style={{ fontSize: '0.8rem', marginBottom: 0, padding: '0.5rem 0.75rem' }}
                        />
                        
                        {/* Remove button */}
                        <button
                          type="button"
                          onClick={() => handleRemoveStep(step.id)}
                          style={{ 
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            borderRadius: '4px',
                            fontSize: '1rem',
                            lineHeight: 1,
                            transition: 'color 0.15s ease, background 0.15s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = 'var(--danger)';
                            e.currentTarget.style.background = 'rgba(255, 107, 107, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'var(--text-muted)';
                            e.currentTarget.style.background = 'transparent';
                          }}
                          title="Remove step"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Add step button */}
                <div style={{ 
                  padding: '0.625rem 1rem', 
                  borderTop: '1px solid var(--border-color)',
                  background: 'var(--surface)'
                }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleAddStep}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}
                  >
                    <span style={{ fontSize: '1rem' }}>+</span> Add Step
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ 
                padding: '1.5rem', 
                border: '2px dashed var(--border-color)', 
                borderRadius: '8px',
                textAlign: 'center',
                color: 'var(--text-muted)'
              }}>
                <div style={{ marginBottom: '0.5rem', fontSize: '1.5rem', opacity: 0.5 }}>📝</div>
                <div style={{ fontSize: '0.875rem' }}>
                  Click "Add Test Steps" to define the step-by-step actions for this test case
                </div>
              </div>
            )}
          </div>
          </div>

          {/* Comments Sidebar */}
          <div style={modalStyles.commentsSidebar}>
            {/* Header */}
            <div style={{
              padding: '1rem 1.25rem',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '1.25rem' }}>💬</span>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Comments
              </h3>
              {comments.length > 0 && (
                <span style={{
                  padding: '0.125rem 0.5rem',
                  background: 'var(--cyan-glow)',
                  color: 'var(--surface)',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  {comments.length}
                </span>
              )}
              {status === 'Needs Update' && comments.length === 0 && (
                <span style={{ 
                  color: 'var(--danger)', 
                  fontSize: '0.7rem',
                  fontWeight: 600 
                }}>
                  *Required
                </span>
              )}
            </div>

            {/* Comments List */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '1rem'
            }}>
              {comments.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem 1rem', 
                  color: 'var(--text-muted)',
                  fontSize: '0.875rem'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.5 }}>💬</div>
                  No comments yet
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {comments.map(comment => (
                    <div key={comment.id} style={{
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '0.25rem'
                      }}>
                        <span style={{ 
                          fontWeight: 600, 
                          fontSize: '0.8rem', 
                          color: 'var(--cyan-glow)' 
                        }}>
                          {comment.userName}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(comment.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                            padding: '0.125rem 0.25rem',
                            fontSize: '0.75rem',
                            borderRadius: '4px',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = 'var(--danger)';
                            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'var(--text-muted)';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Delete comment"
                        >
                          ✕
                        </button>
                      </div>
                      <div style={{ 
                        fontSize: '0.7rem', 
                        color: 'var(--text-muted)',
                        marginBottom: '0.5rem'
                      }}>
                        {new Date(comment.createdAt).toLocaleDateString()} at {new Date(comment.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <p style={{ 
                        margin: 0, 
                        fontSize: '0.875rem', 
                        color: 'var(--text-primary)',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap'
                      }}>
                        {comment.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Comment Input */}
            <div style={{
              padding: '1rem',
              borderTop: '1px solid var(--border-color)'
            }}>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={status === 'Needs Update' 
                  ? "Why does this need an update?..." 
                  : "Add a comment..."}
                className="form-control"
                rows={3}
                style={{ 
                  resize: 'none',
                  marginBottom: '0.75rem',
                  fontSize: '0.875rem',
                  borderColor: status === 'Needs Update' && !newComment.trim() && comments.length === 0 
                    ? 'var(--danger)' 
                    : undefined
                }}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddCommentImmediately}
                disabled={!newComment.trim()}
                style={{ width: '100%' }}
              >
                Add Comment
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={modalStyles.footer}>
          <button
            className="btn btn-secondary"
            onClick={onClose}
            style={{ padding: '0.75rem 1.5rem' }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddTestCase}
            type="button"
            style={{ padding: '0.75rem 2rem', fontWeight: 600 }}
          >
            {editingTestCase ? 'Update Test Case' : 'Create Test Case'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTestCaseModal;

